import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/formatGBP";
import QtyStepper from "@/components/QtyStepper";

export default function CartDrawer() {
  const {
    items, subtotalPennies, drawerOpen, closeDrawer,
    updateQty, removeItem, validateWithServer,
  } = useCart();
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Refresh prices from server whenever the drawer opens
  useEffect(() => {
    if (drawerOpen) {
      validateWithServer();
    }
  }, [drawerOpen, validateWithServer]);

  // ESC to dismiss + body scroll lock + focus management
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
      // Simple focus trap: cycle within the panel.
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'button, a, input, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);

    // Focus the close button on open
    setTimeout(() => closeBtnRef.current?.focus(), 30);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen, closeDrawer]);

  if (!drawerOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-drawer-title"
      data-testid="cart-drawer"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/30 backdrop-blur-sm animate-fade-up"
        onClick={closeDrawer}
        data-testid="cart-drawer-backdrop"
      />

      {/* Panel */}
      <aside
        ref={panelRef}
        className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-white shadow-card-hover flex flex-col"
        style={{
          transform: "translateX(0)",
          animation: "slide-in-right 0.28s cubic-bezier(0.22, 1, 0.36, 1) both",
        }}
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-silver/40">
          <h2
            id="cart-drawer-title"
            className="text-lg font-bold text-navy tracking-tight"
          >
            Your basket
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={closeDrawer}
            aria-label="Close basket"
            data-testid="cart-drawer-close"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-navy hover:bg-ice active:bg-ice/80 transition-colors"
          >
            <X size={22} />
          </button>
        </header>

        {items.length === 0 ? (
          <EmptyState onClose={closeDrawer} />
        ) : (
          <>
            <ul
              className="flex-1 overflow-y-auto divide-y divide-silver/40"
              data-testid="cart-drawer-items"
            >
              {items.map((item) => (
                <li key={item.slug} className="flex gap-4 p-6">
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-ice-light border border-silver/40 shrink-0">
                    {item.image && (
                      <img
                        src={item.image}
                        alt=""
                        className="w-full h-full object-cover blend-product"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy leading-tight">
                      {item.name}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {formatGBP(item.price_pennies)} each
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <QtyStepper
                        value={item.quantity}
                        onChange={(q) => updateQty(item.slug, q)}
                        testIdPrefix={`drawer-qty-${item.slug}`}
                        size="sm"
                      />
                      <p className="text-sm font-semibold text-navy tabular-nums">
                        {formatGBP(item.price_pennies * item.quantity)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.slug)}
                    aria-label={`Remove ${item.name}`}
                    data-testid={`drawer-remove-${item.slug}`}
                    className="self-start inline-flex h-11 w-11 items-center justify-center rounded-full text-muted hover:text-destructive hover:bg-ice active:bg-ice/80 transition-colors -mr-2 -mt-1"
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ul>

            <footer className="border-t border-silver/40 px-6 py-5 space-y-4 bg-ice-light">
              <div className="flex justify-between text-sm">
                <span className="text-muted">Subtotal</span>
                <span
                  className="font-bold text-navy text-base tabular-nums"
                  data-testid="cart-drawer-subtotal"
                >
                  {formatGBP(subtotalPennies)}
                </span>
              </div>
              <p className="text-xs text-muted">
                Shipping calculated at checkout.
              </p>
              <div className="grid grid-cols-1 gap-2">
                <Link
                  to="/cart"
                  onClick={closeDrawer}
                  className="inline-flex items-center justify-center rounded-full border border-navy text-navy h-12 text-sm font-semibold hover:bg-ice transition"
                  data-testid="cart-drawer-view-basket"
                >
                  View basket
                </Link>
                <Link
                  to="/checkout"
                  onClick={closeDrawer}
                  className="inline-flex items-center justify-center rounded-full bg-navy text-white h-12 text-sm font-semibold hover:brightness-110 transition shadow-card"
                  data-testid="cart-drawer-checkout"
                >
                  Checkout
                </Link>
              </div>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}

function EmptyState({ onClose }) {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center text-center px-6 py-12 gap-4"
      data-testid="cart-drawer-empty"
    >
      <div className="w-14 h-14 rounded-full bg-ice flex items-center justify-center">
        <ShoppingBag size={22} className="text-navy" />
      </div>
      <p className="text-navy font-semibold">Your basket is empty.</p>
      <Link
        to="/product"
        onClick={onClose}
        className="inline-flex items-center justify-center rounded-full bg-navy text-white px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition"
        data-testid="cart-drawer-empty-cta"
      >
        Shop PeptiChill
      </Link>
    </div>
  );
}
